import React, { useState, useEffect } from "react";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Printer,
  Plus,
  Search,
  FileText,
  Calendar,
  User,
  X,
  CheckCircle,
  Save,
  Download,
  Upload,
  RefreshCw,
  Coins,
  Receipt,
  Trash2,
  Lock,
  ArrowUpDown,
  CreditCard
} from "lucide-react";
import { Patient, Appointment, FinancialRecord, Installment } from "../types";
import PatientLeafletsViewer from "./PatientLeafletsViewer";

interface SuperAdminDashboardProps {
  user: { email: string; name: string; role: string };
  apiToken: string;
  isIndependent?: boolean;
}

function convertAmountToArabicWords(num: number): string {
  if (!num || num === 0) return "صفر دينار عراقي فقط لا غير";
  const exactMap: Record<number, string> = {
    5000: "خمسة آلاف دينار عراقي فقط لا غير",
    10000: "عشرة آلاف دينار عراقي فقط لا غير",
    15000: "خمسة عشر ألف دينار عراقي فقط لا غير",
    20000: "عشرون ألف دينار عراقي فقط لا غير",
    25000: "خمسة وعشرون ألف دينار عراقي فقط لا غير",
    30000: "ثلاثون ألف دينار عراقي فقط لا غير",
    35000: "خمسة وثلاثون ألف دينار عراقي فقط لا غير",
    40000: "أربعون ألف دينار عراقي فقط لا غير",
    45000: "خمسة وأربعون ألف دينار عراقي فقط لا غير",
    50000: "خمسون ألف دينار عراقي فقط لا غير",
    60000: "ستون ألف دينار عراقي فقط لا غير",
    70000: "سبعون ألف دينار عراقي فقط لا غير",
    75000: "خمسة وسبعون ألف دينار عراقي فقط لا غير",
    80000: "ثمانون ألف دينار عراقي فقط لا غير",
    90000: "تسعون ألف دينار عراقي فقط لا غير",
    100000: "مائة ألف دينار عراقي فقط لا غير",
    125000: "مائة وخمسة وعشرون ألف دينار عراقي فقط لا غير",
    150000: "مائة وخمسون ألف دينار عراقي فقط لا غير",
    200000: "مائتا ألف دينار عراقي فقط لا غير",
    250000: "مائتان وخمسون ألف دينار عراقي فقط لا غير",
    300000: "ثلاثمائة ألف دينار عراقي فقط لا غير",
    350000: "ثلاثمائة وخمسون ألف دينار عراقي فقط لا غير",
    400000: "أربعمائة ألف دينار عراقي فقط لا غير",
    500000: "خمسمائة ألف دينار عراقي فقط لا غير",
    600000: "ستمائة ألف دينار عراقي فقط لا غير",
    700000: "سبعمائة ألف دينار عراقي فقط لا غير",
    750000: "سبعمائة وخمسون ألف دينار عراقي فقط لا غير",
    800000: "ثمانمائة ألف دينار عراقي فقط لا غير",
    900000: "تسعمائة ألف دينار عراقي فقط لا غير",
    1000000: "مليون دينار عراقي فقط لا غير"
  };
  if (exactMap[num]) return exactMap[num];
  const thousands = Math.floor(num / 1000);
  const remainder = num % 1000;
  if (thousands >= 1 && remainder === 0) {
    if (thousands === 1) return "ألف دينار عراقي فقط لا غير";
    if (thousands === 2) return "ألفا دينار عراقي فقط لا غير";
    return `${thousands.toLocaleString('en-US')} ألف دينار عراقي فقط لا غير`;
  }
  return `${num.toLocaleString('en-US')} دينار عراقي فقط لا غير`;
}

export default function SuperAdminDashboard({ user, apiToken, isIndependent = false }: SuperAdminDashboardProps) {
  // Independent financial authentication states
  const [isFinanceLoggedIn, setIsFinanceLoggedIn] = useState(() => {
    // Allow direct access for all authenticated clinical staff (Admin, Doctor, Reception, etc.)
    if (user && user.role && user.role !== "FINANCE_OFFICER") return true;
    return localStorage.getItem("finance_session_authorized") === "true";
  });
  const [financeUsername, setFinanceUsername] = useState("");
  const [financePassword, setFinancePassword] = useState("");
  const [loginError, setLoginError] = useState("");

  // Recovery of finance credentials
  const [isRecovering, setIsRecovering] = useState(false);

  useEffect(() => {
    if (user && user.role && user.role !== "FINANCE_OFFICER") {
      setIsFinanceLoggedIn(true);
    }
  }, [user]);
  const [recoveredCredentials, setRecoveredCredentials] = useState<{username: string; password: string} | null>(null);

  // Elevated Accounts User Management States (Additive ONLY)
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [financeCredentials, setFinanceCredentials] = useState<{username: string; password: string}>({username: "finance", password: "1234"});
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  
  // Account selected for editing state
  const [editingUserIdx, setEditingUserIdx] = useState<number | null>(null);
  const [editingUserEmail, setEditingUserEmail] = useState("");
  const [editingUserName, setEditingUserName] = useState("");
  const [editingUserRole, setEditingUserRole] = useState("");
  const [editingUserPassword, setEditingUserPassword] = useState("");

  // Account selected for finance edit
  const [editingFinanceUser, setEditingFinanceUser] = useState("");
  const [editingFinancePass, setEditingFinancePass] = useState("");

  // Slipped state list
  const [patients, setPatients] = useState<Patient[]>([]);
  const [financials, setFinancials] = useState<FinancialRecord[]>([]);

  // Search, filtration and ordering states
  const [patientSearchQuery, setPatientSearchQuery] = useState("");
  const [ledgerSearchQuery, setLedgerSearchQuery] = useState("");
  const [ledgerTypeFilter, setLedgerTypeFilter] = useState<"ALL" | "RECEIPT" | "PAYMENT">("ALL");
  const [timeFilter, setTimeFilter] = useState<"ALL" | "DAILY" | "MONTHLY" | "YEARLY">("ALL");

  // Receipt creation state
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [customPatientName, setCustomPatientName] = useState("");
  const [customPatientPhone, setCustomPatientPhone] = useState("");
  const [receiptAmount, setReceiptAmount] = useState<number>(10000);
  const [receiptCategory, setReceiptCategory] = useState("كشفية عيادة طبية (Consultation Fee)");
  const [receiptNotes, setReceiptNotes] = useState("");
  
  // Installment plan states
  const [isInstallmentPlan, setIsInstallmentPlan] = useState(false);
  const [downPayment, setDownPayment] = useState<number>(0);
  const [installmentsCount, setInstallmentsCount] = useState<number>(1);

  // Payment/Expense creation state
  const [paymentRecipient, setPaymentRecipient] = useState("");
  const [paymentAmount, setPaymentAmount] = useState<number>(25000);
  const [paymentCategory, setPaymentCategory] = useState("مستلزمات ومواد طبية (Medical Supplies)");
  const [paymentNotes, setPaymentNotes] = useState("");

  // Print layout preview target
  const [activePrintRecord, setActivePrintRecord] = useState<FinancialRecord | null>(null);

  // Recycle Bin states
  const [deletedRecords, setDeletedRecords] = useState<(FinancialRecord & { deletedAt?: string; deletedBy?: string })[]>(() => {
    try {
      const stored = localStorage.getItem("iq_doctor_financial_trash");
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  });
  const [showRecycleBinModal, setShowRecycleBinModal] = useState(false);

  // Notification overlays
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // Active view tabs: 'dashboard' | 'ledger' | 'users' | 'leaflets'
  const [activeSubTab, setActiveSubTab] = useState<"dashboard" | "ledger" | "users" | "leaflets">("dashboard");

  // Load clinic patients and the independent ledger system
  useEffect(() => {
    if (!apiToken) return;

    fetchActivePatients();
    loadFinancialLedger();
    fetch("/api/finance/trash", { headers: { Authorization: `Bearer ${apiToken}` } })
      .then(r => r.ok ? r.json() : [])
      .then((data) => {
        if (Array.isArray(data)) {
          setDeletedRecords(data);
          localStorage.setItem("iq_doctor_financial_trash", JSON.stringify(data));
        }
      })
      .catch(e => console.error("Could not load financial trash", e));

    // EXPERT COMMENTARY: Live listener ensures real-time updates when printed consultation bills auto-register
    const handleLedgerUpdate = () => {
      loadFinancialLedger();
    };

    window.addEventListener("storage", handleLedgerUpdate);
    window.addEventListener("clinical_ledger_updated" as any, handleLedgerUpdate);

    return () => {
      window.removeEventListener("storage", handleLedgerUpdate);
      window.removeEventListener("clinical_ledger_updated" as any, handleLedgerUpdate);
    };
  }, [apiToken]);

  // MANDATORY INCLUSION: Robust clinical system fetching with standard error handlers
  const fetchActivePatients = async () => {
    try {
      const res = await fetch("/api/patients", {
        headers: { Authorization: `Bearer ${apiToken}` },
      });
      if (!res.ok) {
        throw new Error("Failed to receive clinical patient registry.");
      }
      const data = await res.json();
      setPatients(data);
    } catch (err) {
      console.error("Clinical Accounting: Could not load patient roster", err);
    }
  };

  // Keep records persisted on request/on-save in localStorage to fulfill the permanent persistence mandate
  const loadFinancialLedger = async () => {
    try {
      const res = await fetch("/api/finance", {
        headers: { Authorization: `Bearer ${apiToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Fix: Ensure data is sorted newest first
        const sortedData = Array.isArray(data) ? data.sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime()) : [];
        setFinancials(sortedData);
        localStorage.setItem("iq_doctor_standalone_ledger", JSON.stringify(sortedData));
        return;
      }
    } catch (e) {
      console.error("Failed to load financials from API", e);
    }

    const cached = localStorage.getItem("iq_doctor_standalone_ledger");
    if (cached) {
      try {
        const parsedCache = JSON.parse(cached);
        const sortedCache = Array.isArray(parsedCache) ? parsedCache.sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime()) : [];
        setFinancials(sortedCache);
      } catch (e) {
        console.error("Error parsing standalone ledger cache:", e);
      }
    } else {
      setFinancials([]);
    }
  };

  const handlePrintStandardInvoice = () => {
    const printContent = document.getElementById("clinical-invoice-print-layout");
    if (!printContent) return;
    
    const printWindow = window.open("", "", "width=850,height=700");
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>وصل قبض الدكتور واثق الجلبي - ${activePrintRecord?.id}</title>
            <meta charset="utf-8" />
            <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet" />
            <style>
              @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;700;900&display=swap');
              body { 
                font-family: 'Cairo', system-ui, sans-serif; 
                padding: 40px; 
                direction: rtl; 
                background-color: white;
                color: black;
              }
              .border-print { border: 2px solid black !important; }
            </style>
          </head>
          <body>
            <div>
              ${printContent.innerHTML}
            </div>
            <script>
              window.onload = function() {
                window.focus();
                setTimeout(function() {
                  window.print();
                  window.close();
                }, 500);
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    } else {
      window.print();
    }
  };

  const saveLedger = (updatedLedger: FinancialRecord[]) => {
    localStorage.setItem("iq_doctor_standalone_ledger", JSON.stringify(updatedLedger));
    triggerSuccess("تم حفظ السجلات وتأمين قاعدة بيانات النظام المحاسبي فورياً");
  };

  const handleFinanceLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError("");

    try {
      const res = await fetch("/api/auth/finance-login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: financeUsername, password: financePassword })
      });
      const data = await res.json();
      if (!res.ok) {
        setLoginError(data.error || "خطأ في اسم المستخدم أو رمز المرور السنوي لمشرف الصندوق.");
        return;
      }

      localStorage.setItem("finance_session_authorized", "true");
      setIsFinanceLoggedIn(true);
      triggerSuccess("تم تفويض المشرف وتأكيد الدخول المستقل للنظام الحسابي الموحد");
    } catch (err: any) {
      setLoginError("حصل خطأ غير متوقع أثناء تفويض الهوية الطبية الحسابية.");
    }
  };

  const handleRecoverFinanceCredentials = async () => {
    setIsRecovering(true);
    setLoginError("");
    setRecoveredCredentials(null);
    try {
      const res = await fetch("/api/admin/users", {
        headers: { Authorization: `Bearer ${apiToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          const u = data.finance.username;
          const p = data.finance.password;
          setFinanceUsername(u);
          setFinancePassword(p);
          setRecoveredCredentials({ username: u, password: p });
          triggerSuccess("تم استرجاع وعرض بيانات الدخول المحاسبية الفعالة بنجاح وتعبئة الحقول!");
        } else {
          setLoginError("فشل استرجاع البيانات المالية المستهدفة من الخادم.");
        }
      } else {
        setLoginError("رمز تفعيل المشرف الأعلى غير متوفر أو غير مصرح له.");
      }
    } catch (err: any) {
      setLoginError("حدث خطأ أثناء تحميل بيانات الحساب من خادم العيادة.");
    } finally {
      setIsRecovering(false);
    }
  };

  const fetchAllUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const res = await fetch("/api/admin/users", {
        headers: { Authorization: `Bearer ${apiToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setAllUsers(data.users);
          setFinanceCredentials(data.finance);
          setEditingFinanceUser(data.finance.username);
          setEditingFinancePass(data.finance.password);
        }
      }
    } catch (err) {
      console.error("[Clinical User Panel] Failed to load user credentials", err);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (activeSubTab === "users") {
      fetchAllUsers();
    }
  }, [activeSubTab]);

  const handleUpdateStaffUser = async (e: React.FormEvent, oldEmail: string) => {
    e.preventDefault();
    try {
      const res = await fetch("/api/admin/users/update", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`
        },
        body: JSON.stringify({
          type: "STAFF",
          oldEmail,
          newEmail: editingUserEmail,
          name: editingUserName,
          role: editingUserRole,
          password: editingUserPassword
        })
      });

      const data = await res.json();
      if (!res.ok) {
        triggerError(data.error || "فشل تحديث بيانات الحساب الطبي.");
        return;
      }

      triggerSuccess(data.message || "تم حفظ ترخيص وبيانات الكادر بنجاح.");
      setEditingUserIdx(null);
      fetchAllUsers();
    } catch (err: any) {
      triggerError("خطأ غير متوقع أثناء معالجة تعديل الكادر.");
    }
  };

  const handleUpdateFinanceCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch("/api/admin/users/update", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`
        },
        body: JSON.stringify({
          type: "FINANCE",
          financeUsername: editingFinanceUser,
          financePassword: editingFinancePass
        })
      });

      const data = await res.json();
      if (!res.ok) {
        triggerError(data.error || "فشل تحديث بيانات الدخول المالية.");
        return;
      }

      triggerSuccess(data.message || "تم تحديث بيانات الدخول المالية بنجاح.");
      fetchAllUsers();
    } catch (err: any) {
      triggerError("خطأ غير متوقع أثناء معالجة بيانات الصندوق المالية.");
    }
  };

  const handleFinanceLogout = () => {
    localStorage.removeItem("finance_session_authorized");
    setIsFinanceLoggedIn(false);
    triggerSuccess("تم تسجيل الخروج وتأمين نظام الصندوق بنجاح");
  };

  const triggerSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 3500);
  };

  const triggerError = (msg: string) => {
    setErrorMsg(msg);
    setTimeout(() => setErrorMsg(""), 4000);
  };

  // Add customized received amount (إيراد مستلم بالدينار العراقي)
  const handleRegisterReceipt = async (e: React.FormEvent) => {
    e.preventDefault();

    let name = customPatientName.trim();
    let phone = customPatientPhone.trim();
    const pId = selectedPatientId;

    if (pId) {
      const found = patients.find(p => p.id === pId);
      if (found) {
        name = found.name;
        phone = found.phone;
      }
    }

    if (!name) {
      triggerError("يرجى اختيار مريض مسجل أو كتابة الاسم يدوياً لإصدار السند المالي");
      return;
    }

    if (receiptAmount <= 0 || !receiptCategory) {
      triggerError("عذراً دكتور، يرجى تحديد تكلفة الإجراء قبل الحفظ");
      return;
    }
    
    // منع التكرار المطلق (Idempotency)
    const txId = `IQD-TX${Math.floor(1000 + Math.random() * 9000)}`;

    const newRecord: FinancialRecord = {
      id: txId,
      type: "RECEIPT",
      patientId: pId || undefined,
      patientName: name,
      patientPhone: phone || undefined,
      amount: receiptAmount,
      category: receiptCategory + " (مدفوعة بالكامل - Paid)",
      notes: receiptNotes.trim() || "تم الاستلام نقداً من المريض وصرف الوصل",
      date: new Date().toISOString(),
      issuedBy: user.name,
      isInstallmentBased: isInstallmentPlan,
      downPayment: isInstallmentPlan ? downPayment : undefined,
    };

    if (isInstallmentPlan) {
      newRecord.category = receiptCategory + " (نظام أقساط - Installments)";
      const remainingAmount = receiptAmount - downPayment;
      const amountPerInstallment = remainingAmount / installmentsCount;
      const generatedInstallments: Installment[] = [];
      const now = new Date();
      for (let i = 1; i <= installmentsCount; i++) {
        const dueDate = new Date(now);
        dueDate.setMonth(dueDate.getMonth() + i); // Default 1 month apart
        generatedInstallments.push({
          id: `${txId}-INST-${i}`,
          amount: amountPerInstallment,
          dueDate: dueDate.toISOString(),
          status: "PENDING"
        });
      }
      newRecord.installments = generatedInstallments;
      newRecord.notes = receiptNotes.trim() || `تم استلام الدفعة المقدمة (${downPayment} د.ع) والباقي على ${installmentsCount} أقساط.`;
    }

    try {
      const amountToRegister = isInstallmentPlan ? downPayment : receiptAmount;
      const res = await fetch("/api/finance/income", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiToken}` },
        body: JSON.stringify({
          amount: amountToRegister,
          category: newRecord.category,
          description: newRecord.notes,
          patientId: pId || undefined,
          patientName: name
        })
      });
      if (res.ok) {
        const serverRecord = await res.json();
        newRecord.id = serverRecord.id;
      }
    } catch (e) {
      console.error("Failed to post income", e);
    }

    const updated = [newRecord, ...financials];
    setFinancials(updated);
    saveLedger(updated);

    // Refresh state
    setSelectedPatientId("");
    setCustomPatientName("");
    setCustomPatientPhone("");
    setReceiptAmount(10000); // Back to standard 10,000 IQD rate
    setReceiptNotes("");
    setIsInstallmentPlan(false);
    setDownPayment(0);
    setInstallmentsCount(1);
    
    triggerSuccess("تم حفظ المبلغ المستلم بنجاح تام! جاري فتح خيارات فاتورة الطباعة");
    setActivePrintRecord(newRecord); // Launch printable dialog automatically
  };

  const handlePayInstallment = async (recordId: string, installmentId: string) => {
    const recordIndex = financials.findIndex(r => r.id === recordId);
    if (recordIndex === -1) return;
    
    const record = financials[recordIndex];
    if (!record.installments) return;
    
    const installmentIndex = record.installments.findIndex(i => i.id === installmentId);
    if (installmentIndex === -1) return;
    
    const inst = record.installments[installmentIndex];
    if (inst.status === "PAID") return;
    
    const confirm = window.confirm(`هل أنت متأكد من تسديد هذا القسط بقيمة ${inst.amount} د.ع؟`);
    if (!confirm) return;

    // Create a new receipt for the payment so it shows in today's ledger
    const newReceipt: FinancialRecord = {
      id: "tx_" + Date.now().toString(36),
      type: "RECEIPT",
      patientId: record.patientId,
      patientName: record.patientName,
      patientPhone: record.patientPhone,
      amount: inst.amount,
      category: "تسديد قسط (Installment Payment)",
      notes: `تسديد القسط رقم ${installmentIndex + 1} من الفاتورة الأصلية (${record.category})`,
      date: new Date().toISOString(),
      issuedBy: user?.name || "المشرف المالي"
    };

    const updatedRecord = { ...record };
    updatedRecord.installments = [...record.installments];
    updatedRecord.installments[installmentIndex] = {
      ...inst,
      status: "PAID",
      paidDate: new Date().toISOString(),
      receivedBy: user?.name || "المشرف المالي"
    };

    try {
      const res = await fetch("/api/finance/income", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiToken}` },
        body: JSON.stringify({
          amount: newReceipt.amount,
          category: newReceipt.category,
          description: newReceipt.notes,
          patientId: newReceipt.patientId,
          patientName: newReceipt.patientName
        })
      });
      if (res.ok) {
        const serverRecord = await res.json();
        newReceipt.id = serverRecord.id;
      }
      
      // Update original record on server
      await fetch(`/api/finance/${recordId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiToken}` },
        body: JSON.stringify(updatedRecord)
      });
    } catch (e) {
      console.error("Failed to post installment payment", e);
    }

    const updated = [newReceipt, ...financials];
    const finalUpdated = updated.map(f => f.id === recordId ? updatedRecord : f);
    setFinancials(finalUpdated);
    saveLedger(finalUpdated);
    
    triggerSuccess("تم تسديد القسط وتسجيله في إيرادات اليوم بنجاح!");
  };


  // Add customized paid payment (مصاريف عامة للمستشفى)
  const handleRegisterPayment = async (e: React.FormEvent) => {
    e.preventDefault();

    const recipient = paymentRecipient.trim();
    if (!recipient) {
      triggerError("يرجى كتابة اسم الجهة المستلمة أو تفاصيل صرف الحساب");
      return;
    }

    if (paymentAmount <= 0) {
      triggerError("يرجى إدخال مبلغ الصرف بشكل صحيح بالدينار العراقي");
      return;
    }

    const newRecord: FinancialRecord = {
      id: `IQD-TX${Math.floor(1000 + Math.random() * 9000)}`,
      type: "PAYMENT",
      recipientName: recipient,
      patientName: `[صرف خارجي] ${recipient}`,
      amount: paymentAmount,
      category: paymentCategory,
      notes: paymentNotes.trim() || "سند دفع مصاريف عامة مرخص من المشرف الأعلى",
      date: new Date().toISOString(),
      issuedBy: user.name
    };

    try {
      const res = await fetch("/api/finance/expense", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiToken}` },
        body: JSON.stringify({
          amount: paymentAmount,
          category: paymentCategory,
          description: paymentNotes.trim() || "Manual Expense entry"
        })
      });
      if (res.ok) {
        const serverRecord = await res.json();
        newRecord.id = serverRecord.id;
      }
    } catch (e) {
      console.error("Failed to post expense", e);
    }

    const updated = [newRecord, ...financials];
    setFinancials(updated);
    saveLedger(updated);

    // Reset standard expense template
    setPaymentRecipient("");
    setPaymentAmount(25000);
    setPaymentNotes("");
    
    triggerSuccess("تم تقييد المصروفات وتأكيد الدفع بالصندوق السندات");
  };

  // Safely erase records with soft delete to recycle bin
  const handleDeleteRecord = async (id: string) => {
    if (confirm("هل أنت متأكد من نقل هذا القيد إلى سلة المهملات المحاسبية؟ يمكنك مراجعة الفاتورة واسترجاعها لاحقاً عند الحاجة.")) {
      const target = financials.find(f => f.id === id);
      if (target) {
        const trashed = [{ ...target, deletedAt: new Date().toISOString(), deletedBy: user?.name || "المشرف المالي" }, ...deletedRecords];
        setDeletedRecords(trashed);
        localStorage.setItem("iq_doctor_financial_trash", JSON.stringify(trashed));
      }
      try {
        await fetch(`/api/finance/${id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${apiToken}` }
        });
      } catch (e) {
        console.error("Failed to delete finance record", e);
      }
      const updated = financials.filter(f => f.id !== id);
      setFinancials(updated);
      saveLedger(updated);
      triggerSuccess("🗑️ تم نقل الفاتورة إلى سلة المهملات بنجاح (يمكنك مراجعتها واسترجاعها بأي وقت)");
    }
  };

  const handleRestoreRecord = async (id: string) => {
    const toRestore = deletedRecords.find(t => t.id === id);
    if (!toRestore) return;
    const { deletedAt, deletedBy, ...clean } = toRestore;
    const nextTrash = deletedRecords.filter(t => t.id !== id);
    setDeletedRecords(nextTrash);
    localStorage.setItem("iq_doctor_financial_trash", JSON.stringify(nextTrash));

    const updatedFin = [clean, ...financials];
    setFinancials(updatedFin);
    saveLedger(updatedFin);

    try {
      await fetch(`/api/finance/trash/restore/${id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${apiToken}` }
      });
    } catch (e) {
      console.error("Failed cloud restore", e);
    }
    triggerSuccess("♻️ تم إسترجاع الفاتورة بنجاح وعادت فوراً لدفتر اليومية!");
  };

  const handlePurgeRecord = async (id: string) => {
    if (!confirm("⚠️ تحذير: هل أنت متأكد من الحذف النهائي القطعي لهذه الفاتورة؟ لا يمكن التراجع بعد ذلك.")) return;
    const nextTrash = deletedRecords.filter(t => t.id !== id);
    setDeletedRecords(nextTrash);
    localStorage.setItem("iq_doctor_financial_trash", JSON.stringify(nextTrash));
    try {
      await fetch(`/api/finance/trash/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${apiToken}` }
      });
    } catch (e) {
      console.error("Failed cloud purge", e);
    }
    triggerSuccess("🔥 تم الحذف النهائي للفاتورة من أرشيف المحذوفات");
  };

  // Keep backup JSON files upon request always (الأحتفاظ بالسجلات عند الطلب دائماً)
  const handleDownloadLedgerJSON = () => {
    try {
      const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(financials, null, 2));
      const downloadAnchor = document.createElement("a");
      downloadAnchor.setAttribute("href", dataStr);
      downloadAnchor.setAttribute("download", `IQ_Doctor_Financial_Ledger_${new Date().toISOString().split("T")[0]}.json`);
      document.body.appendChild(downloadAnchor);
      downloadAnchor.click();
      downloadAnchor.remove();
      triggerSuccess("تم تصدير نسخة مستقلة وكاملة من السجلات المحاسبية كـ JSON بنجاح");
    } catch (e) {
      triggerError("فشل أثناء تصدير ملف السجلات.");
    }
  };

  const handleUploadLedgerJSON = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileReader = new FileReader();
    if (e.target.files && e.target.files[0]) {
      fileReader.readAsText(e.target.files[0], "UTF-8");
      fileReader.onload = (event) => {
        try {
          const parsed = JSON.parse(event.target?.result as string);
          if (Array.isArray(parsed)) {
            setFinancials(parsed);
            saveLedger(parsed);
            triggerSuccess("تم استيراد واستعادة الدفاتر الحسابية والوصلات بنجاح من الملف الخارجي");
          } else {
            triggerError("صيغة الملف غير صالحة. يرجى توفير ملف مالي صحيح.");
          }
        } catch (err) {
          triggerError("فشل في قراءة وتفسير محتويات الملف المالي.");
        }
      };
    }
  };

  // Compute calculated values
  const now = new Date();
  const timeFilteredFinancials = financials.filter(record => {
    if (timeFilter === "ALL") return true;
    const recordDate = new Date(record.date);
    if (timeFilter === "DAILY") {
      return recordDate.toDateString() === now.toDateString();
    }
    if (timeFilter === "MONTHLY") {
      return recordDate.getMonth() === now.getMonth() && recordDate.getFullYear() === now.getFullYear();
    }
    if (timeFilter === "YEARLY") {
      return recordDate.getFullYear() === now.getFullYear();
    }
    return true;
  });

  const totalReceipts = timeFilteredFinancials
    .filter(f => f.type === "RECEIPT")
    .reduce((sum, f) => {
       let total = f.isInstallmentBased ? (f.downPayment || 0) : f.amount;
       if (f.isInstallmentBased && f.installments) {
          total += f.installments.filter(inst => inst.status === "PAID").reduce((s, inst) => s + inst.amount, 0);
       }
       return sum + total;
    }, 0);

  const totalPayments = timeFilteredFinancials
    .filter(f => f.type === "PAYMENT")
    .reduce((sum, f) => sum + f.amount, 0);

  const netTreasury = totalReceipts - totalPayments;

  // Filter lists based on inputs
  const filteredPatients = patients.filter(p =>
    (p.name || "").toLowerCase().includes(patientSearchQuery.toLowerCase()) ||
    (p.phone || "").includes(patientSearchQuery) ||
    (p.id || "").toLowerCase().includes(patientSearchQuery.toLowerCase())
  );

  const filteredLedger = timeFilteredFinancials.filter(record => {
    const term = ledgerSearchQuery.toLowerCase();
    const matchText = (record.patientName || record.recipientName || "").toLowerCase().includes(term) ||
      (record.notes || "").toLowerCase().includes(term) ||
      (record.category || "").toLowerCase().includes(term) ||
      (record.id || "").toLowerCase().includes(term);

    const matchType = ledgerTypeFilter === "ALL" || record.type === ledgerTypeFilter;
    return matchText && matchType;
  });

  if (!isFinanceLoggedIn) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex flex-col justify-center items-center px-4 py-8" dir="rtl">
        {/* Toast Alert Success/Error notifications inside login view */}
        {successMsg && (
          <div className="fixed bottom-6 left-6 z-50 bg-slate-900 text-white font-medium text-xs px-5 py-4 rounded-xl shadow-2xl border border-slate-800 border-r-4 border-r-emerald-500 flex items-center gap-3 animate-in fade-in slide-in-from-bottom duration-300 max-w-sm">
            <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0" />
            <div className="flex flex-col text-right">
              <span className="font-bold text-slate-100 mb-0.5">عملية ناجحة • Saved Successfully</span>
              <span className="text-[10px] text-slate-400 leading-tight">{successMsg}</span>
            </div>
          </div>
        )}

        {errorMsg && (
          <div className="fixed bottom-6 left-6 z-50 bg-rose-950 text-white font-medium text-xs px-5 py-4 rounded-xl shadow-2xl border border-rose-900 border-r-4 border-r-rose-500 flex items-center gap-3 animate-in fade-in slide-in-from-bottom duration-300 max-w-sm">
            <X className="w-5 h-5 text-rose-400 shrink-0" />
            <div className="flex flex-col text-right">
              <span className="font-bold text-red-200 mb-0.5">تنبيه النظام المحاسبي</span>
              <span className="text-[10px] text-rose-300 leading-tight">{errorMsg}</span>
            </div>
          </div>
        )}

        {/* Clinic Brand Banner */}
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-transparent w-12 h-12 flex items-center justify-center rounded-full overflow-hidden">
            <img src="/logo.png" className="w-full h-full object-cover" />
          </div>
          <div>
            <h1 className="text-lg font-bold tracking-tight text-white font-sans">الدكتور واثق الجلبي</h1>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-400">نظام الحسابات والصندوق المستقل • Safe Treasury</p>
          </div>
        </div>

        {/* Back Link to Visitor Home */}
        <div className="mb-6 text-center">
          <a
            href="#"
            className="inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-full text-slate-300 hover:text-white bg-slate-800 border border-slate-700 transition"
          >
            <X className="w-3.5 h-3.5" />
            <span>الرجوع إلى الصفحة الرئيسية للزوار</span>
          </a>
        </div>

        <div className="w-full max-w-md bg-[#131B2E] border border-slate-800 rounded-xl p-6 shadow-lg animate-fade-in text-slate-200">
          <div className="flex items-center gap-2 mb-1">
            <Lock className="w-4 h-4 text-amber-500 animate-pulse" />
            <h2 className="text-base font-bold text-white font-sans">منظومة الحسابات الموحدة المستقلة</h2>
          </div>
          <p className="text-xs mb-6 text-slate-400">بوابة الدخول الآمن لتسجيل المبالغ والوصولات بالدينار العراقي للمشرف المالي.</p>

          {loginError && (
            <div className="mb-4 bg-rose-950/40 border border-rose-900/60 text-rose-200 text-xs px-3 py-2 rounded-md font-medium text-right">
              {loginError}
            </div>
          )}

          <form onSubmit={handleFinanceLogin} className="space-y-4" autoComplete="off">
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-amber-500">اسم مستخدم الحساب المحاسبي</label>
              <input
                type="text"
                placeholder="مثلاً: admin أو finance"
                value={financeUsername}
                onChange={(e) => setFinanceUsername(e.target.value)}
                className="w-full text-xs px-3 py-2 bg-[#0B0F19] border border-slate-800 focus:border-amber-500 rounded-md outline-none duration-150 text-white"
                required
                autoComplete="off"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-amber-500">رمز المرور أو الرقم التعريفي المشرف (ID/Password)</label>
              <input
                type="password"
                placeholder="أدخل رمز تفويض الحسابات..."
                value={financePassword}
                onChange={(e) => setFinancePassword(e.target.value)}
                className="w-full text-xs px-3 py-2 bg-[#0B0F19] border border-slate-800 focus:border-amber-500 rounded-md outline-none duration-150 text-white"
                required
                autoComplete="new-password"
              />
              <span className="block text-[9px] text-slate-500 mt-1.5 leading-normal text-right">
                تلميح أمني للتفويض: أدخل نفس اسم المستخدم وكلمة المرور الخاصة بحساب السوبر أدمن.
              </span>
            </div>

            <button
              type="submit"
              className="w-full h-10 bg-[#EAB308] hover:bg-yellow-500 text-[#070A13] font-bold rounded-lg flex items-center justify-center gap-2 cursor-pointer transition select-none text-xs shadow-[0_0_15px_rgba(234,179,8,0.2)]"
            >
              <CheckCircle className="w-4 h-4" />
              تأكّيد الترخيص والدخول المستقل
            </button>
          </form>
        </div>

        <div className="mt-8 text-center max-w-xs text-[10px] text-slate-500">
          <p>© 2026 IQ-doctor clinic. Restricted financial ledger and corporate accounts system.</p>
        </div>
      </div>
    );
  }

  // Installments summary for selected patient
  const patientInstallmentRecords = selectedPatientId ? financials.filter(f => f.patientId === selectedPatientId && f.isInstallmentBased && f.installments && f.installments.length > 0) : [];
  let totalInstallmentsOrigAmount = 0;
  let totalInstallmentsPaid = 0;
  let totalInstallmentsRemaining = 0;
  
  patientInstallmentRecords.forEach(rec => {
    totalInstallmentsOrigAmount += rec.amount || 0;
    totalInstallmentsPaid += rec.downPayment || 0;
    if (rec.installments) {
      rec.installments.forEach(inst => {
        if (inst.status === "PAID") {
          totalInstallmentsPaid += inst.amount;
        } else {
          totalInstallmentsRemaining += inst.amount;
        }
      });
    }
  });

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-16 px-4 sm:px-6" dir="rtl">
      
      {/* Toast Alert Success notifications */}
      {successMsg && (
        <div className="fixed bottom-6 left-6 z-50 bg-slate-900 text-white font-medium text-xs px-5 py-4 rounded-xl shadow-2xl border border-slate-800 border-r-4 border-r-emerald-500 flex items-center gap-3 animate-in fade-in slide-in-from-bottom duration-300 max-w-sm">
          <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0" />
          <div className="flex flex-col text-right">
            <span className="font-bold text-slate-100 mb-0.5">عملية ناجحة • Saved Successfully</span>
            <span className="text-[10px] text-slate-400 leading-tight">{successMsg}</span>
          </div>
        </div>
      )}

      {errorMsg && (
        <div className="fixed bottom-6 left-6 z-50 bg-rose-950 text-white font-medium text-xs px-5 py-4 rounded-xl shadow-2xl border border-rose-900 border-r-4 border-r-rose-500 flex items-center gap-3 animate-in fade-in slide-in-from-bottom duration-300 max-w-sm">
          <X className="w-5 h-5 text-rose-400 shrink-0" />
          <div className="flex flex-col text-right">
            <span className="font-bold text-red-200 mb-0.5">تنبيه النظام المحاسبي</span>
            <span className="text-[10px] text-rose-300 leading-tight">{errorMsg}</span>
          </div>
        </div>
      )}

      {/* Header Info Hero */}
      <div className="max-w-7xl mx-auto pt-6 mb-8">
        <div className="bg-gradient-to-l from-slate-900 to-indigo-950 rounded-2xl p-6 sm:p-8 hover:shadow-indigo-950/10 hover:shadow-2xl transition duration-500 text-white border border-slate-850 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-64 h-64 bg-indigo-505/10 rounded-full blur-3xl -translate-x-12 -translate-y-12"></div>
          
          <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-amber-400 text-[#070A13] text-[9.5px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full shadow-sm">
                  🔐 حساب المشرف الأعلى المستقل • STANDALONE SUPERADMIN
                </span>
                <span className="bg-white/10 text-emerald-300 text-[10px] font-bold px-2 py-0.5 rounded">
                  عيادة IQ-Doctor
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-white mb-2 font-sans">
                النظام المحاسبي والمالي المستقل للعيادة
              </h1>
              <p className="text-slate-300 text-xs sm:text-sm max-w-xl font-medium leading-relaxed">
                لوحة المشرف الأعلى لإدارة مقبوضات المرضى والمدفوعات والمستلم بالدينار العراقي، وإصدار وتصدير فواتير كشفية 10,000 د.ع أو أي مبالغ مخصصة فوراً مع ميزية الاحتفاظ بالسجلات والأرشفة الدائمة.
              </p>
            </div>

            <div className="flex flex-wrap gap-2.5 shrink-0">
              <button
                onClick={handleDownloadLedgerJSON}
                className="bg-slate-800 hover:bg-slate-700 text-slate-100 border border-slate-700 text-xs py-2 px-3.5 rounded-xl font-bold transition flex items-center gap-1.5 focus:ring-1 focus:ring-amber-400 cursor-pointer animate-none"
                title="تصدير السجل المالي"
              >
                <Download className="w-3.5 h-3.5" />
                تحميل السجل (Backup)
              </button>

              <label className="bg-slate-800 hover:bg-slate-700 text-slate-100 border border-slate-700 text-xs py-2 px-3.5 rounded-xl font-bold transition flex items-center gap-1.5 focus:ring-1 focus:ring-amber-400 cursor-pointer text-center relative animate-none">
                <Upload className="w-3.5 h-3.5" />
                <span>استرجاع البيانات</span>
                <input
                  type="file"
                  accept=".json"
                  onChange={handleUploadLedgerJSON}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
              </label>

              <button
                onClick={handleFinanceLogout}
                className="bg-rose-950/40 hover:bg-rose-900/40 text-rose-300 border border-rose-900/50 text-xs py-2 px-3.5 rounded-xl font-bold transition flex items-center gap-1.5 cursor-pointer animate-none"
                title="تسجيل الخروج من النظام المحاسبي وتأمينه"
              >
                <Lock className="w-3.5 h-3.5 text-rose-400" />
                خروج آمن (Log Out)
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Budget Widgets */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
        {/* Treasury receipts card */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-slate-300 transition shadow-sm flex flex-col justify-between">
          <div className="flex justify-between items-center bg-white">
            <span className="text-[11px] text-slate-400 font-extrabold uppercase tracking-wider">الحصيلة والإيرادات (المستلم)</span>
            <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-xl border border-emerald-100">
              <TrendingUp className="w-4 h-4" />
            </div>
          </div>
          <div className="my-3">
            <h3 className="font-mono text-2xl font-black text-slate-900">
              {totalReceipts.toLocaleString("en-US")}
            </h3>
            <span className="text-[10px] font-bold text-emerald-600">دينار عراقي (IQD)</span>
          </div>
          <p className="text-[10px] text-slate-400">مجموع المبالغ المحصلة من الكشفيات والخدمات المنجزة للمرضى.</p>
        </div>

        {/* Expenses card */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-slate-300 transition shadow-sm flex flex-col justify-between">
          <div className="flex justify-between items-center bg-white">
            <span className="text-[11px] text-slate-400 font-extrabold uppercase tracking-wider">إجمالي التكاليف والمصاريف (المدفوع)</span>
            <div className="p-2.5 bg-rose-50 text-rose-500 rounded-xl border border-rose-100">
              <TrendingDown className="w-4 h-4" />
            </div>
          </div>
          <div className="my-3">
            <h3 className="font-mono text-2xl font-black text-rose-700">
              {totalPayments.toLocaleString("en-US")}
            </h3>
            <span className="text-[10px] font-bold text-rose-500">دينار عراقي (IQD)</span>
          </div>
          <p className="text-[10px] text-slate-400">مجموع المبالغ المخصصة للموظفين والمورّدين والمصاريف الجارية.</p>
        </div>

        {/* Balance treasury card */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-slate-300 transition shadow-sm flex flex-col justify-between">
          <div className="flex justify-between items-center bg-white">
            <span className="text-[11px] text-slate-400 font-extrabold uppercase tracking-wider">صافي رصيد الصندوق المحاسبي</span>
            <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl border border-indigo-100">
              <DollarSign className="w-4 h-4" />
            </div>
          </div>
          <div className="my-3">
            <h3 className={`font-mono text-2xl font-black ${netTreasury >= 0 ? "text-indigo-900" : "text-rose-600"}`}>
              {netTreasury.toLocaleString("en-US")}
            </h3>
            <span className="text-[10px] font-bold text-indigo-600">دينار عراقي (IQD)</span>
          </div>
          <p className="text-[10px] text-slate-400">رصيد السيولة الحالية المتوافر مخصوماً منه المصروفات المباشرة.</p>
        </div>
      </div>

      {/* Tabs segment */}
      <div className="max-w-7xl mx-auto flex gap-1.5 border-b border-slate-200 mb-6 font-sans text-xs">
        <button
          onClick={() => setActiveSubTab("dashboard")}
          className={`py-3 px-5 font-extrabold border-b-2 hover:bg-slate-100/50 transition cursor-pointer select-none rounded-t-xl ${
            activeSubTab === "dashboard"
              ? "border-slate-900 text-slate-900 bg-white shadow-sm font-black"
              : "border-transparent text-slate-400"
          }`}
        >
          شاشة التسجيل المالي (المقبوضات والمصاريف)
        </button>
        <button
          onClick={() => setActiveSubTab("ledger")}
          className={`py-3 px-5 font-extrabold border-b-2 hover:bg-slate-100/50 transition cursor-pointer select-none rounded-t-xl ${
            activeSubTab === "ledger"
              ? "border-slate-900 text-slate-900 bg-white shadow-sm font-black"
              : "border-transparent text-slate-400"
          }`}
        >
          دفتر اليومية ومؤشر الوصولات التفضيلية ({filteredLedger.length})
        </button>
        <button
          onClick={() => setActiveSubTab("users")}
          className={`py-3 px-5 font-extrabold border-b-2 hover:bg-slate-100/50 transition cursor-pointer select-none rounded-t-xl ${
            activeSubTab === "users"
              ? "border-slate-900 text-slate-900 bg-white shadow-sm font-black"
              : "border-transparent text-slate-400"
          }`}
        >
          إدارة الحسابات وكلمات المرور (Account & User Management)
        </button>
        <button
          onClick={() => setActiveSubTab("leaflets")}
          className={`py-3 px-5 font-extrabold border-b-2 hover:bg-slate-100/50 transition cursor-pointer select-none rounded-t-xl ${
            activeSubTab === "leaflets"
              ? "border-amber-500 text-amber-600 bg-amber-50/40 shadow-sm font-black"
              : "border-transparent text-slate-400"
          }`}
        >
          🖼️ إدارة صور النشرات التثقيفية (BAD)
        </button>
      </div>

      {/* Dashboard Sub-Tab Content */}
      <div className="max-w-7xl mx-auto">
        {activeSubTab === "dashboard" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Record Receipt Form (إيراد مستلم بالكامل من المرضى) */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
              <div className="border-b border-slate-100 pb-3 mb-5 flex items-center gap-2">
                <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg">
                  <Coins className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-extrabold text-sm text-slate-900">تسجيل المقبوضات وتوليد الفاتورة (مستلم)</h3>
                  <p className="text-[10px] text-slate-400 mt-0.5">تسجيل الدخل المالي للمرضى والقدرة على تعديل المبالغ بأي سعر بالدينار العراقي.</p>
                </div>
              </div>

              <form onSubmit={handleRegisterReceipt} className="space-y-4">
                <div>
                  <label className="block text-[11.5px] font-bold text-slate-700 mb-1">المريض المستحق (مسجل بنظام الحسابات)</label>
                  <div className="relative mb-2">
                    <span className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-slate-400 pointer-events-none">
                      <Search className="w-3.5 h-3.5" />
                    </span>
                    <input
                      type="text"
                      placeholder="ابحث سرياً عن مريض في قاعدة كشوفات العيادة..."
                      value={patientSearchQuery}
                      onChange={(e) => {
                        setPatientSearchQuery(e.target.value);
                        setSelectedPatientId(""); // clear selection if they type
                        if (e.target.value === "") {
                          setCustomPatientName("");
                          setCustomPatientPhone("");
                        }
                      }}
                      className="w-full bg-slate-55 border border-slate-300 rounded-lg pr-8 pl-3 py-1.5 text-xs outline-none focus:bg-white"
                    />
                  </div>

                  {patientSearchQuery.trim().length > 0 && !selectedPatientId && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredPatients.length > 0 ? (
                        filteredPatients.map((p) => (
                          <div
                            key={p.id}
                            className="px-3 py-2 text-xs hover:bg-indigo-50 cursor-pointer border-b border-slate-100 last:border-0"
                            onClick={() => {
                              setSelectedPatientId(p.id);
                              setPatientSearchQuery(`[${p.id.substring(0, 6).toUpperCase()}] ${p.name} - ${p.phone || "بدون رقم هاتف مضاف"}`);
                              setCustomPatientName("");
                              setCustomPatientPhone("");
                            }}
                          >
                            <div className="font-semibold text-slate-800">{p.name}</div>
                            <div className="text-[10px] text-slate-500 font-mono flex gap-2">
                              <span>{p.phone || "بدون رقم هاتف"}</span>
                              <span>• ID: {p.id.substring(0, 6).toUpperCase()}</span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-4 text-xs text-center text-slate-500">
                          لم يتم العثور على مريض بهذا الاسم...
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {!selectedPatientId && (
                  <div className="p-4 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                    <span className="text-[10px] uppercase tracking-wider font-extrabold text-slate-450 block mb-2">سند استلام لمريض خارجي يدوياً:</span>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10.5px] font-semibold text-slate-600 mb-1">اسم المريض الكامل</label>
                        <input
                          type="text"
                          placeholder="مثلاً: حسن علي الخفاجي"
                          value={customPatientName}
                          onChange={(e) => setCustomPatientName(e.target.value)}
                          className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[10.5px] font-semibold text-slate-600 mb-1">رقم الهاتف للتوثيق</label>
                        <input
                          type="text"
                          placeholder="077XXXXXXXX"
                          value={customPatientPhone}
                          onChange={(e) => setCustomPatientPhone(e.target.value)}
                          className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-mono outline-none"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {selectedPatientId && patientInstallmentRecords.length > 0 && (
                  <div className="p-4 bg-indigo-50/50 rounded-xl border border-indigo-100 mb-4">
                    <h4 className="text-xs font-bold text-indigo-900 mb-3 flex items-center gap-1.5">
                      <CreditCard className="w-4 h-4 text-indigo-600" />
                      موقف الأقساط للمريض المحدد
                    </h4>
                    
                    <div className="grid grid-cols-3 gap-3 mb-4 text-center">
                      <div className="bg-white p-2 rounded-lg border border-slate-200 shadow-sm">
                        <span className="block text-[10px] text-slate-500 mb-1">إجمالي الفواتير</span>
                        <span className="block text-xs font-bold text-slate-800">{totalInstallmentsOrigAmount.toLocaleString()} د.ع</span>
                      </div>
                      <div className="bg-white p-2 rounded-lg border border-emerald-100 shadow-sm">
                        <span className="block text-[10px] text-emerald-600 mb-1">المدفوع الكلي</span>
                        <span className="block text-xs font-bold text-emerald-700">{totalInstallmentsPaid.toLocaleString()} د.ع</span>
                      </div>
                      <div className="bg-white p-2 rounded-lg border border-rose-100 shadow-sm">
                        <span className="block text-[10px] text-rose-600 mb-1">المتبقي (الديون)</span>
                        <span className="block text-xs font-bold text-rose-700">{totalInstallmentsRemaining.toLocaleString()} د.ع</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {patientInstallmentRecords.map(rec => (
                        <div key={rec.id} className="bg-white border border-slate-200 rounded-lg p-3">
                          <div className="flex justify-between items-center mb-2 border-b border-slate-100 pb-2">
                            <span className="text-[11px] font-bold text-slate-800">{rec.category}</span>
                            <span className="text-[10px] text-slate-500">{new Date(rec.date).toLocaleDateString("ar-IQ")}</span>
                          </div>
                          <div className="space-y-1.5">
                            {rec.installments?.map((inst, idx) => (
                              <div key={inst.id} className="flex justify-between items-center text-[11px] hover:bg-slate-50 p-1 rounded transition">
                                <span className="text-slate-600">
                                  قسط {idx + 1} ({inst.amount.toLocaleString()} د.ع)
                                  <span className="text-[9px] text-slate-400 block mt-0.5">مستحق في: {new Date(inst.dueDate).toLocaleDateString("ar-IQ")}</span>
                                </span>
                                {inst.status === "PAID" ? (
                                  <span className="text-emerald-600 font-bold bg-emerald-50 px-2 py-1 rounded border border-emerald-100 text-[10px]">تم التسديد</span>
                                ) : (
                                  <button 
                                    type="button" 
                                    onClick={() => handlePayInstallment(rec.id, inst.id)}
                                    className="text-white bg-indigo-600 hover:bg-indigo-700 px-3 py-1.5 rounded shadow-sm font-bold text-[10px] transition cursor-pointer"
                                  >
                                    تسديد الآن
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[11.5px] font-bold text-slate-700 mb-1">المبلغ بالدينار العراقي (IQD)</label>
                    <input
                      type="number"
                      value={receiptAmount}
                      onChange={(e) => setReceiptAmount(Number(e.target.value))}
                      className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-sm font-black font-mono outline-none text-emerald-850 focus:ring-1 focus:ring-indigo-550"
                      min={0}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[11.5px] font-bold text-slate-700 mb-1">خدمات وأجور الكشف</label>
                    <select
                      value={receiptCategory}
                      onChange={(e) => setReceiptCategory(e.target.value)}
                      className="w-full bg-white border border-slate-300 rounded-lg px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-indigo-550"
                    >
                      <option value="كشفية عيادة طبية (Consultation Fee)">كشفية عيادة طبية (10,000 د.ع)</option>
                      <option value="استشارة تجميل متطورة (Laser Skin Check)">حقن وجلسة ليزر تخصصية</option>
                      <option value="جلسة علاج أسنان (Dental Care)">عنب وتبييض وحشو عصب أسنان</option>
                      <option value="أجور مختبر وتحاليل (Clinical Lab Tests)">نتائج تحاليل وفحوصات داخلية</option>
                      <option value="مراجعة وفحص دوري (Routine Follow-up)">فحص ومتابعة مجانية أو منخفضة</option>
                    </select>
                  </div>
                </div>

                {/* IQD Instant Tweak Buttons */}
                <div>
                  <span className="block text-[10px] font-extrabold text-slate-400 mb-1.5 uppercase">تغيير وتهيئة أجور سريعة بالدينار العراقي:</span>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      onClick={() => setReceiptAmount(10000)}
                      className={`px-3 py-1 rounded text-[10.5px] font-mono font-bold transition border cursor-pointer ${
                        receiptAmount === 10000 ? "bg-emerald-600 text-white border-emerald-600" : "bg-slate-50 text-slate-700 hover:bg-slate-100 border-slate-200"
                      }`}
                    >
                      10,000 د.ع (كشفية اعتيادية)
                    </button>
                    <button
                      type="button"
                      onClick={() => setReceiptAmount(25000)}
                      className={`px-3 py-1 rounded text-[10.5px] font-mono font-bold transition border cursor-pointer ${
                        receiptAmount === 25000 ? "bg-emerald-600 text-white border-emerald-600" : "bg-slate-50 text-slate-700 hover:bg-slate-100 border-slate-200"
                      }`}
                    >
                      25,000 د.ع
                    </button>
                    <button
                      type="button"
                      onClick={() => setReceiptAmount(50000)}
                      className={`px-3 py-1 rounded text-[10.5px] font-mono font-bold transition border cursor-pointer ${
                        receiptAmount === 50000 ? "bg-emerald-600 text-white border-emerald-600" : "bg-slate-50 text-slate-700 hover:bg-slate-100 border-slate-200"
                      }`}
                    >
                      50,000 د.ع
                    </button>
                    <button
                      type="button"
                      onClick={() => setReceiptAmount(100000)}
                      className={`px-3 py-1 rounded text-[10.5px] font-mono font-bold transition border cursor-pointer ${
                        receiptAmount === 100000 ? "bg-emerald-600 text-white border-emerald-600" : "bg-slate-50 text-slate-700 hover:bg-slate-100 border-slate-200"
                      }`}
                    >
                      100,000 د.ع
                    </button>
                  </div>
                </div>

                {/* Installments Plan Toggle */}
                <div className="bg-slate-50 border border-slate-200 p-3 rounded-lg">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isInstallmentPlan}
                          onChange={(e) => setIsInstallmentPlan(e.target.checked)}
                          className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500 border-slate-300"
                        />
                        <span className="text-[11.5px] font-bold text-slate-800">تفعيل نظام الأقساط لهذه الفاتورة</span>
                      </label>
                      <p className="text-[9px] text-slate-500 mt-0.5 pr-6">تقسيم المبلغ الإجمالي إلى دفعة مقدمة وأقساط لاحقة.</p>
                    </div>
                    {isInstallmentPlan && (
                      <div className="flex items-center gap-3 mt-2 sm:mt-0 pr-6 sm:pr-0">
                        <div>
                          <label className="block text-[10px] font-bold text-slate-600 mb-1">الدفعة المقدمة (IQD)</label>
                          <input
                            type="number"
                            value={downPayment}
                            onChange={(e) => setDownPayment(Number(e.target.value))}
                            min={0}
                            max={receiptAmount}
                            className="w-24 bg-white border border-slate-300 rounded px-2 py-1 text-xs font-mono outline-none focus:ring-1 focus:ring-indigo-550"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-600 mb-1">عدد الأقساط</label>
                          <select
                            value={installmentsCount}
                            onChange={(e) => setInstallmentsCount(Number(e.target.value))}
                            className="w-20 bg-white border border-slate-300 rounded px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-indigo-550"
                          >
                            {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => <option key={n} value={n}>{n}</option>)}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-[11.5px] font-bold text-slate-705 mb-1">ملاحظات الفاتورة والتفصيل</label>
                  <textarea
                    rows={2}
                    placeholder="ملاحظات وتفاصيل إضافية سيتم طباعتها للمريض..."
                    value={receiptNotes}
                    onChange={(e) => setReceiptNotes(e.target.value)}
                    className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:ring-1 focus:ring-indigo-550"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs py-2.5 px-4 rounded-xl font-bold shadow-md hover:shadow-lg transition flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <Printer className="w-3.5 h-3.5" />
                  تسجيل المستلم + توليد فاتورة طباعة فورية
                </button>
              </form>
            </div>

            {/* Record Expense/Payment Form (مصاريف ونشاطات تشغيلية) */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
              <div className="border-b border-slate-100 pb-3 mb-5 flex items-center gap-2">
                <div className="p-2 bg-rose-50 text-rose-500 rounded-lg">
                  <TrendingDown className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-extrabold text-sm text-slate-900">تسجيل مدفوعات ونفقات جارية (المدفوع)</h3>
                  <p className="text-[10px] text-slate-400 mt-0.5">تسجيل تكاليف صيانة، شراء معدات طبية، رواتب أو فواتير بالدينار العراقي.</p>
                </div>
              </div>

              <form onSubmit={handleRegisterPayment} className="space-y-4">
                <div>
                  <label className="block text-[11.5px] font-bold text-slate-707 mb-1">اسم المستلم / الجهة المدفوع لها</label>
                  <input
                    type="text"
                    placeholder="مثال: شركة الرواد للمستلزمات الطبية أو المحاسب محمد"
                    value={paymentRecipient}
                    onChange={(e) => setPaymentRecipient(e.target.value)}
                    className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:ring-1 focus:ring-indigo-550"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[11.5px] font-bold text-slate-707 mb-1">المبلغ المطلوب صرفه (IQD)</label>
                    <input
                      type="number"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(Number(e.target.value))}
                      className="w-full bg-white border border-slate-300 rounded-lg px-3 py-1.5 text-sm font-black font-mono text-rose-600 outline-none focus:ring-1 focus:ring-rose-550"
                      min={0}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-[11.5px] font-bold text-slate-707 mb-1">فئة وباب المصروفات</label>
                    <select
                      value={paymentCategory}
                      onChange={(e) => setPaymentCategory(e.target.value)}
                      className="w-full bg-white border border-slate-300 rounded-lg px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-indigo-550"
                    >
                      <option value="مستلزمات ومواد طبية (Medical Supplies)">مواد ومستلزمات طبية</option>
                      <option value="رواتب الموظفين (Clinic Salaries)">رواتب الممرضين والموظفين والكوادر</option>
                      <option value="صيانة أجهزة العيادة (Device Maintenance)">صيانة وإصلاح أجهزة ليزر وجراحة</option>
                      <option value="أجور الطاقة والخدمات (Energy/Rent)">أجور تشغيل المولد، الكهرباء أو الإيجار</option>
                      <option value="نثرية وضيافة إدارية (Miscellaneous Expense)">نثرية وضيافة عامة ومطبوعات</option>
                    </select>
                  </div>
                </div>

                {/* Instant expense selector tweak */}
                <div>
                  <span className="block text-[10px] font-extrabold text-slate-400 mb-1.5 uppercase">تغيير وتهيئة مبالغ صرف عاجلة:</span>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      onClick={() => setPaymentAmount(25000)}
                      className={`px-3 py-1 rounded text-[10.5px] font-mono font-bold transition border cursor-pointer ${
                        paymentAmount === 25000 ? "bg-rose-600 text-white border-rose-600" : "bg-slate-50 text-slate-700 hover:bg-slate-100 border-slate-200"
                      }`}
                    >
                      25,000 د.ع
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentAmount(50000)}
                      className={`px-3 py-1 rounded text-[10.5px] font-mono font-bold transition border cursor-pointer ${
                        paymentAmount === 50000 ? "bg-rose-600 text-white border-rose-600" : "bg-slate-50 text-slate-700 hover:bg-slate-100 border-slate-200"
                      }`}
                    >
                      50,000 د.ع
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentAmount(150000)}
                      className={`px-3 py-1 rounded text-[10.5px] font-mono font-bold transition border cursor-pointer ${
                        paymentAmount === 150000 ? "bg-rose-600 text-white border-rose-600" : "bg-slate-50 text-slate-700 hover:bg-slate-100 border-slate-200"
                      }`}
                    >
                      150,000 د.ع
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentAmount(250000)}
                      className={`px-3 py-1 rounded text-[10.5px] font-mono font-bold transition border cursor-pointer ${
                        paymentAmount === 250000 ? "bg-rose-600 text-white border-rose-600" : "bg-slate-50 text-slate-700 hover:bg-slate-100 border-slate-200"
                      }`}
                    >
                      250,000 د.ع
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-[11.5px] font-bold text-slate-707 mb-1">البيان المبرر لعملية الصرف المعتمدة</label>
                  <textarea
                    rows={2}
                    placeholder="التفاصيل المحاسبية لعملية الصرف (تعد مرجعاً لتدقيق الحسابات)..."
                    value={paymentNotes}
                    onChange={(e) => setPaymentNotes(e.target.value)}
                    className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:ring-1 focus:ring-rose-500"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-[#1E293B] hover:bg-[#0F172A] text-white text-xs py-2.5 px-4 rounded-xl font-bold shadow-md transition flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <Trash2 className="w-3.5 h-3.5 text-rose-400" />
                  تسجيل المدفوعات وتأكيد سحب المبلغ من الصندوق
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Ledger Sub-Tab Content */}
        {activeSubTab === "ledger" && (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-100 pb-4 mb-6">
              <div>
                <h3 className="font-extrabold text-sm text-slate-900">سجل المعاملات والقيود المالية للصندوق الموحد</h3>
                <p className="text-[10px] text-slate-400 mt-1">دفتر اليومية لتصنيف المقبوضات وتكاليف العيادة مع إمكانيات فرز واسترجاع المعاملات.</p>
              </div>

              {/* Dynamic Filters */}
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative">
                  <span className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-slate-400 pointers-events-none">
                    <Search className="w-3 px-1 h-3" />
                  </span>
                  <input
                    type="text"
                    placeholder="ابحث بالاسم، القيمة، أو البند..."
                    value={ledgerSearchQuery}
                    onChange={(e) => setLedgerSearchQuery(e.target.value)}
                    className="bg-slate-50 border border-slate-200 text-xs rounded-lg pr-8 pl-3 py-1.5 outline-none focus:bg-white"
                  />
                </div>

                <select
                  value={ledgerTypeFilter}
                  onChange={(e) => setLedgerTypeFilter(e.target.value as any)}
                  className="bg-slate-50 border border-slate-200 text-xs rounded-lg px-2.5 py-1.5 outline-none"
                >
                  <option value="ALL">جميع القيود المالية</option>
                  <option value="RECEIPT">مقبوضات وإيرادات فقط</option>
                  <option value="PAYMENT">مدفوعات ومصاريف فقط</option>
                </select>

                <select
                  value={timeFilter}
                  onChange={(e) => setTimeFilter(e.target.value as any)}
                  className="bg-slate-50 border border-slate-200 text-xs rounded-lg px-2.5 py-1.5 outline-none"
                >
                  <option value="ALL">كل الأوقات</option>
                  <option value="DAILY">يومي (اليوم)</option>
                  <option value="MONTHLY">شهري (هذا الشهر)</option>
                  <option value="YEARLY">سنوي (هذه السنة)</option>
                </select>

                <button
                  type="button"
                  onClick={() => setShowRecycleBinModal(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-800 font-black text-xs rounded-lg border border-rose-200 shadow-sm transition cursor-pointer select-none"
                >
                  <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                  <span>سلة الفواتير المحذوفة</span>
                  <span className="bg-rose-600 text-white font-mono text-[10px] px-2 py-0.5 rounded-full font-extrabold shadow-sm">
                    {deletedRecords.length}
                  </span>
                </button>
              </div>
            </div>

            {/* Standard Ledger Table representation */}
            <div className="overflow-x-auto">
              <table className="w-full text-right border-collapse text-xs">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-400 font-extrabold tracking-wider uppercase bg-slate-50/50">
                    <th className="py-2.5 px-3 text-center">رمز المعاملة</th>
                    <th className="py-2.5 px-3">طبيعة العملية</th>
                    <th className="py-2.5 px-3">الجهة / اسم المريض</th>
                    <th className="py-2.5 px-3">المبلغ د.ع (IQD)</th>
                    <th className="py-2.5 px-3">التصنيف المحاسبي</th>
                    <th className="py-2.5 px-3">التاريخ والوقت</th>
                    <th className="py-2.5 px-3">المرخص</th>
                    <th className="py-2.5 px-3 text-center">خيارات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLedger.map((row) => (
                    <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50/60 transition">
                      <td className="py-3 px-3 font-mono font-bold text-slate-400 text-center">{row.id}</td>
                      <td className="py-3 px-3">
                        {row.type === "RECEIPT" ? (
                          <span className="bg-emerald-58 bg-emerald-50 text-emerald-800 border border-emerald-100 font-bold text-[9.5px] px-2.5 py-0.5 rounded-full">
                            مستلم • إيراد
                          </span>
                        ) : (
                          <span className="bg-rose-58 bg-rose-50 text-rose-800 border border-rose-100 font-bold text-[9.5px] px-2.5 py-0.5 rounded-full">
                            مدفوع • صرف
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-3 font-extrabold text-slate-900">
                        {row.patientName || (row.type === "RECEIPT" ? "مراجعة كشفية عامة / نقدي" : "مصروفات عامة / تشغيل عيادة")}
                      </td>
                      <td className="py-3 px-3 font-mono font-extrabold text-slate-900">
                        {row.amount.toLocaleString("en-US")} د.ع
                      </td>
                      <td className="py-3 px-3 text-slate-500 font-semibold">{row.category}</td>
                      <td className="py-3 px-3 text-slate-400 font-medium">
                        {new Date(row.date).toLocaleString("ar-IQ", { dateStyle: "short", timeStyle: "short" })}
                      </td>
                      <td className="py-3 px-3 text-slate-500 font-medium">{row.issuedBy}</td>
                      <td className="py-3 px-3 text-center">
                        <div className="flex gap-2 justify-center">
                          {row.type === "RECEIPT" && (
                            <button
                              onClick={() => setActivePrintRecord(row)}
                              className="bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 p-1.5 rounded-lg shadow-sm hover:text-indigo-600 transition"
                              title="طباعة السند والوصل"
                            >
                              <Printer className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteRecord(row.id)}
                            className="bg-white hover:bg-rose-50 text-rose-500 hover:text-rose-700 border border-slate-200 p-1.5 rounded-lg shadow-sm transition"
                            title="حذف القيد نهائياً"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredLedger.length === 0 && (
                    <tr>
                      <td colSpan={8} className="py-12 text-center text-slate-400 font-bold">
                        لا توجد هناك سجلات مالية متطابثة مع تصفية البحث الحالي بالدينار العراقي.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Dynamic User Accounts Management Sub-Tab (Additive ONLY) */}
        {activeSubTab === "users" && (
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom duration-300" dir="rtl">
            {/* Finance credentials block */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
              <div className="border-b border-rose-100 pb-3 mb-5 flex justify-between items-center bg-white">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-amber-50 text-amber-600 rounded-lg">
                    <Lock className="w-5 h-5 animate-pulse" />
                  </div>
                  <div>
                    <h3 className="font-extrabold text-sm text-slate-900">تعديل بيانات دخول النظام المالي (Finance System Accounts)</h3>
                    <p className="text-[10px] text-slate-400 mt-0.5">تعديل اسم المستخدم وكلمة المرور الخاصة بلوحة الدخول الحسابي المستقل.</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleUpdateFinanceCredentials} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[11.5px] font-bold text-slate-700 mb-1.5">اسم المستخدم المالي الجديد</label>
                  <input
                    type="text"
                    value={editingFinanceUser}
                    onChange={(e) => setEditingFinanceUser(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-xs font-mono font-bold outline-none focus:ring-1 focus:ring-amber-550"
                    required
                  />
                </div>
                <div>
                  <label className="block text-[11.5px] font-bold text-slate-700 mb-1.5">كلمة مرور النظام المالي الجديدة</label>
                  <input
                    type="text"
                    value={editingFinancePass}
                    onChange={(e) => setEditingFinancePass(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-xs font-mono font-bold outline-none focus:ring-1 focus:ring-amber-550"
                    required
                  />
                </div>
                <div className="md:col-span-2 flex justify-end">
                  <button
                    type="submit"
                    className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-extrabold text-xs py-2 px-5 rounded-lg transition shadow-sm cursor-pointer"
                  >
                    حفظ وتأمين حساب الصندوق المالي
                  </button>
                </div>
              </form>
            </div>

            {/* Patients and clinical personnel block */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
              <div className="border-b border-indigo-100 pb-3 mb-5 bg-white">
                <h3 className="font-extrabold text-sm text-slate-900">إدارة حسابات مستخدمي كادر العيادة الطبي</h3>
                <p className="text-[10px] text-slate-400 mt-1">تعديل البريد الإلكتروني والبروتوكول المرخص ورمز المرور السري لكل فرد بالعيادة.</p>
              </div>

              {isLoadingUsers ? (
                <div className="py-12 text-center text-slate-400 font-bold text-xs">
                  جاري جلب تفاصيل الحسابات وكادر العيادة الطبي...
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-[11.5px] font-bold text-slate-700 mb-2">اختر حساب الموظف لتعديل بياناته</label>
                    <select
                      className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-xs font-bold outline-none focus:ring-1 focus:ring-indigo-500 cursor-pointer"
                      value={editingUserIdx !== null ? editingUserIdx : ""}
                      onChange={(e) => {
                        const idx = e.target.value === "" ? null : parseInt(e.target.value);
                        if (idx !== null) {
                          const usr = allUsers[idx];
                          setEditingUserIdx(idx);
                          setEditingUserEmail(usr.email);
                          setEditingUserName(usr.name);
                          setEditingUserRole(usr.role);
                          setEditingUserPassword(usr.password);
                        } else {
                          setEditingUserIdx(null);
                        }
                      }}
                    >
                      <option value="">-- اضغط هنا لاختيار المستخدم من القائمة --</option>
                      {allUsers.map((usr, idx) => (
                        <option key={usr.email} value={idx}>{usr.name} — ({usr.role})</option>
                      ))}
                    </select>
                  </div>

                  {editingUserIdx !== null && (
                    <form onSubmit={(e) => handleUpdateStaffUser(e, allUsers[editingUserIdx].email)} className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 p-5 border border-slate-200 rounded-xl mt-4 relative">
                      <div className="absolute top-0 right-0 bg-indigo-100 text-indigo-700 text-[9px] font-bold px-2 py-0.5 rounded-bl-lg rounded-tr-xl">
                        وضع التعديل
                      </div>
                      <div>
                        <label className="block text-[10.5px] font-bold text-slate-700 mb-1">الاسم الكامل للموظف</label>
                        <input
                          type="text"
                          value={editingUserName}
                          onChange={(e) => setEditingUserName(e.target.value)}
                          className="w-full border border-slate-300 rounded-lg px-2.5 py-2 text-xs font-bold focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-[10.5px] font-bold text-slate-700 mb-1">الدور / الرتبة والوظيفة</label>
                        <select
                          value={editingUserRole}
                          onChange={(e) => setEditingUserRole(e.target.value)}
                          className="w-full border border-slate-300 rounded-lg px-2.5 py-2 text-xs font-bold focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                        >
                          <option value="SUPER_ADMIN">SUPER_ADMIN (المشرف الأعلى)</option>
                          <option value="PHYSICIAN">PHYSICIAN (الطبيب الأخصائي)</option>
                          <option value="RECEPTIONIST">RECEPTIONIST (موظفة الاستقبال)</option>
                          <option value="NURSE">NURSE (كادر التمريض)</option>
                          <option value="LAB_TECH">LAB_TECH (المختبر)</option>
                          <option value="RADIOLOGY_TECH">RADIOLOGY_TECH (الأشعة)</option>
                          <option value="PHARMACIST">PHARMACIST (الصيدلية)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10.5px] font-bold text-slate-700 mb-1">اسم الدخول (البريد الإلكتروني)</label>
                        <input
                          type="text"
                          value={editingUserEmail}
                          onChange={(e) => setEditingUserEmail(e.target.value)}
                          className="w-full border border-slate-300 rounded-lg px-2.5 py-2 text-xs font-mono text-left focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                          dir="ltr"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-[10.5px] font-bold text-slate-700 mb-1">رمز المرور المعتمد</label>
                        <input
                          type="text"
                          value={editingUserPassword}
                          onChange={(e) => setEditingUserPassword(e.target.value)}
                          className="w-full border border-slate-300 rounded-lg px-2.5 py-2 text-xs font-mono text-left focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                          dir="ltr"
                          required
                        />
                      </div>
                      <div className="md:col-span-2 flex justify-end gap-2 mt-2">
                        <button
                          type="button"
                          onClick={() => setEditingUserIdx(null)}
                          className="bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold px-4 py-2 rounded-lg text-xs transition cursor-pointer"
                        >
                          إلغاء التعديل
                        </button>
                        <button
                          type="submit"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-6 py-2 rounded-lg text-xs transition shadow-sm cursor-pointer"
                        >
                          تأكيد حفظ التغييرات 
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {activeSubTab === "leaflets" && (
          <div className="bg-white rounded-3xl p-2 sm:p-4 shadow-xl border border-slate-200 mt-2 overflow-hidden">
            <PatientLeafletsViewer onBack={() => setActiveSubTab("dashboard")} isAdmin={true} />
          </div>
        )}
      </div>

      {/* Printable Invoice Dialog Overlay */}
      {activePrintRecord && (
        <div className="fixed inset-0 bg-[#0F172A]/70 backdrop-blur-xs flex items-center justify-center p-4 z-50 overflow-y-auto no-print">
          <div className="bg-white rounded-3xl w-full max-w-2xl border border-slate-200 shadow-2xl relative flex flex-col my-8">
            
            {/* Overlay Header Bar */}
            <div className="flex justify-between items-center bg-slate-900 px-6 py-4 rounded-t-3xl text-white">
              <div className="flex items-center gap-2">
                <Receipt className="w-5 h-5 text-amber-400" />
                <span className="font-extrabold text-sm tracking-tight">معاينة وصل الاستلام والطباعة • Clinical Receipt Preview</span>
              </div>
              <button
                type="button"
                onClick={() => setActivePrintRecord(null)}
                className="text-slate-400 hover:text-white transition p-1 hover:bg-slate-800 rounded-lg cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Print Controls Ribbon */}
            <div className="bg-slate-50 border-b border-slate-200 px-6 py-3 flex justify-between items-center gap-4 text-xs font-semibold text-slate-700">
              <span>يرجى مراجعة تفاصيل الوصل الطبي قبل توجيهه لطابعة العيادة.</span>
              <button
                onClick={handlePrintStandardInvoice}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-4 py-2 rounded-lg shadow flex items-center gap-1.5 transition cursor-pointer select-none"
              >
                <Printer className="w-3.5 h-3.5" />
                تأكيد وبدء الطباعة (Print)
              </button>
            </div>

            {/* Core A4 Medical Receipt Layout to capture with DOM print */}
            <div className="p-8 overflow-y-auto max-h-[80vh]">
              <div id="clinical-invoice-print-layout" className="bg-white p-6 sm:p-8 rounded-2xl border-2 border-black font-sans text-black" dir="rtl">
                
                {/* Printable Header */}
                <div className="flex justify-between items-start border-b-2 border-black pb-4 mb-6">
                  <div>
                    <h2 className="text-xl font-bold tracking-tight text-black mb-1">عيادة الدكتور واثق عبدالخالق الجلبي</h2>
                    <h1 className="text-lg font-black text-slate-800">وصل استلام أجور كشفية مراجعة</h1>
                    <p className="text-[10px] font-mono text-slate-500 mt-1">
                      No: {activePrintRecord.id} • Date: {new Date(activePrintRecord.date).toLocaleDateString("ar-IQ", { dateStyle: "long" })}
                    </p>
                  </div>
                  <div className="text-left" dir="ltr">
                    <h3 className="font-black text-md uppercase">Dr. Wathiq Al-Chalabi Clinic</h3>
                    <p className="text-[10px] text-slate-500">Clinical Accounting System</p>
                    <p className="text-[9px] font-mono mt-0.5 text-slate-400">Baghdad, Iraq</p>
                  </div>
                </div>

                {/* Details Content Block */}
                <div className="space-y-4 mb-8 text-xs sm:text-sm">
                  {/* Subject details card */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 border border-slate-250 rounded-xl p-4">
                      <span className="text-[10px] text-slate-450 font-extrabold uppercase block mb-1">الجهة المسددة / استلمنا من:</span>
                      <p className="font-black text-slate-900 text-md">{activePrintRecord.patientName || "مراجعة كشفية عامة / نقدي"}</p>
                    </div>
                    {(() => {
                      const receivedVal = (activePrintRecord.isInstallmentBased && activePrintRecord.downPayment !== undefined)
                        ? activePrintRecord.downPayment
                        : activePrintRecord.amount;
                      return (
                        <div className="bg-slate-55 border border-slate-250 rounded-xl p-4 flex flex-col justify-center items-center text-center">
                          <span className="text-[10px] text-slate-450 font-extrabold uppercase block mb-0.5">المبلغ المحصل والإيفاء بالدينار:</span>
                          <p className="font-black text-xl text-emerald-800 font-mono">
                            {receivedVal.toLocaleString("en-US")} د.ع
                          </p>
                          <span className="text-[10px] font-bold text-slate-700 mt-1">{convertAmountToArabicWords(receivedVal)}</span>
                        </div>
                      );
                    })()}
                  </div>

                  <div className="border border-slate-250 rounded-xl p-4">
                    <span className="text-[10px] text-slate-450 font-extrabold uppercase block mb-1">الخدمة الطبية المكافئة:</span>
                    <p className="font-bold text-slate-900 text-xs sm:text-sm">{activePrintRecord.category}</p>
                  </div>

                  {/* Comprehensive Installments Financial Position Block */}
                  {(() => {
                    const parentPlan = financials.find(f => 
                      f.isInstallmentBased && f.installments && f.installments.length > 0 && 
                      ((activePrintRecord.patientId && f.patientId === activePrintRecord.patientId) || 
                       (activePrintRecord.patientName && f.patientName === activePrintRecord.patientName) || 
                       f.id === activePrintRecord.id)
                    );
                    if (!parentPlan || (!activePrintRecord.category?.includes("أقساط") && !activePrintRecord.category?.includes("Installment") && !activePrintRecord.notes?.includes("قسط") && !activePrintRecord.notes?.includes("أقساط") && !activePrintRecord.isInstallmentBased)) {
                      return null;
                    }
                    const totalCost = parentPlan.amount || 0;
                    const totalPaid = (parentPlan.downPayment || 0) + (parentPlan.installments?.filter(i => i.status === "PAID").reduce((s, i) => s + i.amount, 0) || 0);
                    const remainingBalance = Math.max(0, totalCost - totalPaid);
                    const totalCount = parentPlan.installments?.length || 0;
                    const paidCount = parentPlan.installments?.filter(i => i.status === "PAID").length || 0;

                    return (
                      <div className="bg-emerald-50/70 border-2 border-emerald-500/30 rounded-2xl p-4 my-4 shadow-sm text-right">
                        <div className="flex items-center justify-between border-b border-emerald-200/80 pb-2 mb-3">
                          <span className="text-xs font-black text-emerald-950">📊 كشف الموقف المالي لنظام الأقساط والذمة المالية للمريض</span>
                          <span className="bg-emerald-200/80 text-emerald-950 text-[10px] font-black px-2.5 py-0.5 rounded-full">
                            {paidCount} من {totalCount} قسط مسدّد
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div className="bg-white border border-slate-200 rounded-xl p-2.5 shadow-sm">
                            <span className="text-[9px] text-slate-500 font-extrabold block mb-1">إجمالي التكلفة الكلية:</span>
                            <span className="font-mono font-black text-slate-900 text-sm md:text-base">{totalCost.toLocaleString("en-US")} د.ع</span>
                          </div>
                          <div className="bg-white border border-emerald-300 rounded-xl p-2.5 shadow-sm">
                            <span className="text-[9px] text-emerald-700 font-extrabold block mb-1">المحصّل والمقدم (المسدّد):</span>
                            <span className="font-mono font-black text-emerald-700 text-sm md:text-base">{totalPaid.toLocaleString("en-US")} د.ع</span>
                          </div>
                          <div className="bg-white border border-rose-200 rounded-xl p-2.5 shadow-sm">
                            <span className="text-[9px] text-rose-700 font-extrabold block mb-1">المتبقي ذمة (المطالب به):</span>
                            <span className="font-mono font-black text-rose-700 text-sm md:text-base">{remainingBalance.toLocaleString("en-US")} د.ع</span>
                          </div>
                        </div>
                        <p className="text-[9px] text-slate-600 font-medium mt-2 text-center">
                          * هذا الكشف يعكس الرصيد المالي المجمّع لبرنامج الأقساط المعتمد والذمة الملتزم بها في عيادة الدكتور واثق الجلبي.
                        </p>
                      </div>
                    );
                  })()}

                  {activePrintRecord.notes && (
                    <div className="border border-slate-250 rounded-xl p-4">
                      <span className="text-[10px] text-slate-450 font-extrabold uppercase block mb-1">ملاحظات وبيان المعاملة:</span>
                      <p className="text-slate-700 text-xs leading-relaxed">{activePrintRecord.notes}</p>
                    </div>
                  )}
                </div>

                {/* Sign Stamp footer */}
                <div className="flex justify-between items-end border-t border-black/30 pt-6 mt-12">
                  <div className="text-center">
                    <span className="text-[9px] text-slate-450 block mb-6">توقيع الموظف المسؤول / المشرف الأعلى</span>
                    <p className="border-t border-black/80 pt-1 text-xs font-bold w-36 mx-auto">توقيع وختم القبض</p>
                  </div>
                  <div className="text-left font-sans text-[10px]" dir="ltr">
                    <p className="font-extrabold">IQ-DOCTOR ADMINISTRATIVE SEREVICES</p>
                    <p className="text-slate-400 mt-0.5">Approved and Audited via Superadmin</p>
                  </div>
                </div>

              </div>
            </div>

            {/* Dialog Footer Actions */}
            <div className="bg-slate-50 px-6 py-4 rounded-b-3xl flex justify-end gap-2 border-t border-slate-200">
              <button
                type="button"
                onClick={() => setActivePrintRecord(null)}
                className="bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 font-bold px-4 py-2 rounded-xl text-xs transition cursor-pointer select-none"
              >
                إغلاق (Close)
              </button>
              <button
                onClick={handlePrintStandardInvoice}
                className="bg-slate-900 hover:bg-slate-850 text-white font-bold px-5 py-2 rounded-xl text-xs shadow transition cursor-pointer select-none"
              >
                تأكيد وطباعة الفاتورة
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Recycle Bin Interactive Modal */}
      {showRecycleBinModal && (
        <div className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl max-w-4xl w-full overflow-hidden text-right flex flex-col max-h-[85vh]" dir="rtl">
            <div className="bg-rose-950 text-white px-6 py-4 flex items-center justify-between shadow-sm">
              <div className="flex items-center gap-2.5">
                <Trash2 className="w-6 h-6 text-rose-300" />
                <h3 className="font-extrabold text-base sm:text-lg">سلة المهملات المحاسبية وأرشيف الفواتير المحذوفة</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowRecycleBinModal(false)}
                className="text-rose-300 hover:text-white transition p-1 hover:bg-rose-900 rounded-lg cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 bg-slate-50/50">
              <p className="text-xs font-bold text-slate-600 mb-4 bg-amber-50 border border-amber-200 p-3 rounded-xl flex items-center gap-2">
                ℹ️ أي قيد أو فاتورة يتم حذفها بالخطأ أو لغرض التدقيق تُحفظ هنا آلياً. يمكنك استرجاع الفاتورة فوراً لتعود بكامل قيمتها وحركاتها لدفتر اليومية.
              </p>

              {deletedRecords.length === 0 ? (
                <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center my-6">
                  <Trash2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="font-bold text-slate-500 text-sm">سلة المهملات فارغة حالياً. لا توجد أي فواتير محذوفة!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {deletedRecords.map((item) => (
                    <div key={item.id} className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm hover:border-slate-300 transition flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div className="space-y-1 text-right flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs font-black text-slate-900 bg-slate-100 px-2.5 py-0.5 rounded-lg border border-slate-200">
                            {item.id}
                          </span>
                          <span className={`text-[11px] font-black px-2.5 py-0.5 rounded-full ${item.type === "RECEIPT" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
                            {item.type === "RECEIPT" ? "مستلم • إيراد" : "مدفوع • صرف"}
                          </span>
                          <span className="text-xs font-extrabold text-slate-900">
                            {item.patientName || (item.type === "RECEIPT" ? "مراجعة كشفية عامة" : "مصروفات عامة")}
                          </span>
                        </div>
                        <p className="text-xs font-bold text-slate-700">
                          {item.category} — <span className="font-mono font-black text-emerald-800">{item.amount.toLocaleString("en-US")} د.ع</span>
                        </p>
                        {item.notes && <p className="text-[11px] text-slate-500 font-medium">📝 {item.notes}</p>}
                        <div className="flex items-center gap-4 text-[10px] text-slate-400 font-semibold pt-1 border-t border-slate-100">
                          <span>📅 تاريخ الحذف: {item.deletedAt ? new Date(item.deletedAt).toLocaleString("ar-IQ", { dateStyle: "short", timeStyle: "short" }) : "سابق"}</span>
                          <span>👤 حُذفت بواسطة: {item.deletedBy || "المشرف المالي"}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                        <button
                          type="button"
                          onClick={() => handleRestoreRecord(item.id)}
                          className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs rounded-xl shadow-sm flex items-center gap-1.5 transition cursor-pointer select-none"
                        >
                          <RefreshCw className="w-3.5 h-3.5" />
                          <span>إسترجاع الفاتورة</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handlePurgeRecord(item.id)}
                          className="px-3 py-2 bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold text-xs rounded-xl border border-rose-200 transition cursor-pointer select-none"
                          title="حذف نهائي من الأرشيف"
                        >
                          🔥 حذف قطعي
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-slate-100 px-6 py-4 border-t border-slate-200 flex justify-end">
              <button
                type="button"
                onClick={() => setShowRecycleBinModal(false)}
                className="bg-slate-900 hover:bg-slate-850 text-white font-extrabold px-6 py-2 rounded-xl text-xs transition cursor-pointer shadow"
              >
                إغلاق النافذة
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
